<script setup lang="ts">
/**
 * UiCarousel Component
 * 
 * A generic carousel component with:
 * - Direct slot for items
 * - Optional infinite auto-scroll
 * - Manual navigation (left/right)
 * - Masked fade edges
 * - Responsive item widths via CSS
 */

interface Props {
  autoScroll?: boolean
  autoScrollSpeed?: number // Pixels per frame
  showNavigation?: boolean
  infinite?: boolean
  gradientEdges?: boolean
  paused?: boolean
  containerTag?: string
}

const props = withDefaults(defineProps<Props>(), {
  autoScroll: false,
  autoScrollSpeed: 1,
  showNavigation: false,
  infinite: false,
  gradientEdges: true,
  paused: false,
  containerTag: 'div'
})

const scrollContainer = ref<HTMLElement | null>(null)
const autoScrollAnimation = ref<number | null>(null)
const isHovered = ref(false)

const canScroll = ref(false)
let resizeObserver: ResizeObserver | null = null

const stopAutoScroll = () => {
  if (autoScrollAnimation.value) {
    cancelAnimationFrame(autoScrollAnimation.value)
    autoScrollAnimation.value = null
  }
}

const scroll = (direction: 'left' | 'right') => {
  if (!scrollContainer.value) return
  const scrollAmount = Math.min(scrollContainer.value.offsetWidth * 0.8, 400)
  
  // Check for End of Scroll to Loop
  if (direction === 'right') {
      const { scrollLeft, scrollWidth, offsetWidth } = scrollContainer.value
      // If we are close to end (within 10px), loop back to start
      if (scrollLeft + offsetWidth >= scrollWidth - 10) {
          scrollContainer.value.scrollTo({ left: 0, behavior: 'smooth' })
          return
      }
  }

  scrollContainer.value.scrollBy({ 
    left: direction === 'left' ? -scrollAmount : scrollAmount, 
    behavior: 'smooth' 
  })
}



// Effective speed depends on hover state and external pause
const currentSpeed = computed(() => (isHovered.value || props.paused) ? 0 : props.autoScrollSpeed)

const cachedDimensions = ref({ scrollWidth: 0, offsetWidth: 0 })

const checkScroll = () => {
    if (scrollContainer.value) {
        cachedDimensions.value = {
            scrollWidth: scrollContainer.value.scrollWidth,
            offsetWidth: scrollContainer.value.offsetWidth
        }
        canScroll.value = cachedDimensions.value.scrollWidth > cachedDimensions.value.offsetWidth
    }
}

const startAutoScroll = () => {
  if (!props.autoScroll) return
  
  const animate = () => {
    if (!scrollContainer.value || !props.autoScroll) return
    
    // Only scroll if speed is > 0
    if (currentSpeed.value > 0) {
      const { scrollLeft } = scrollContainer.value
      // Use cached dimensions to avoid forcing reflow (reading layout props after write)
      const { scrollWidth, offsetWidth } = cachedDimensions.value
      
      // Fallback if cache is zero (first run)
      const effectiveScrollWidth = scrollWidth || scrollContainer.value.scrollWidth
      const effectiveOffsetWidth = offsetWidth || scrollContainer.value.offsetWidth
      
      if (props.infinite && scrollLeft >= (effectiveScrollWidth / 2)) {
        scrollContainer.value.scrollLeft = 0
      } else if (!props.infinite && scrollLeft + effectiveOffsetWidth >= effectiveScrollWidth) {
        scrollContainer.value.scrollLeft = 0
      } else {
        scrollContainer.value.scrollLeft += currentSpeed.value
      }
    }
    
    autoScrollAnimation.value = requestAnimationFrame(animate)
  }
  
  autoScrollAnimation.value = requestAnimationFrame(animate)
}

onMounted(() => {
  if (props.autoScroll) startAutoScroll()
  
  // Observe size changes
  resizeObserver = new ResizeObserver(() => {
      checkScroll()
  })
  if (scrollContainer.value) {
      resizeObserver.observe(scrollContainer.value)
      checkScroll()
      setTimeout(checkScroll, 500)
  }
})

onUnmounted(() => {
  stopAutoScroll()
  resizeObserver?.disconnect()
})

// No need to stop/start the loop on hover anymore, the loop handles the speed check
const isDown = ref(false)
const startX = ref(0)
const scrollLeftStart = ref(0) // Renamed from scrollLeft to avoid collision if any
const isDragging = ref(false) // To distinguish click vs drag

const startDrag = (e: MouseEvent) => {
    if (!scrollContainer.value) return
    e.preventDefault() // Prevent text selection/native drag
    isDown.value = true
    isDragging.value = false
    scrollContainer.value.classList.add('active')
    startX.value = e.pageX
    scrollLeftStart.value = scrollContainer.value.scrollLeft
    
    // Pause auto-scroll on interaction
    stopAutoScroll()
    
    // Add global listeners
    window.addEventListener('mouseup', stopDrag)
    window.addEventListener('mousemove', doDrag)
}

const stopDrag = () => {
    isDown.value = false
    if (scrollContainer.value) {
        scrollContainer.value.classList.remove('active')
    }
    
    // Remove global listeners
    window.removeEventListener('mouseup', stopDrag)
    window.removeEventListener('mousemove', doDrag)
    
    // Resume auto-scroll if enabled and not hovered
    if (props.autoScroll && !props.paused && !isHovered.value) {
         startAutoScroll()
    }
    
    // Reset dragging flag after a short delay so click handlers can check it
    setTimeout(() => {
        isDragging.value = false
    }, 0)
}

const doDrag = (e: MouseEvent) => {
    if (!isDown.value || !scrollContainer.value) return
    e.preventDefault()
    const x = e.pageX
    const walk = (x - startX.value) * 1.5 // Scroll speed multiplier
    
    if (Math.abs(walk) > 5) {
        isDragging.value = true
    }
    
    scrollContainer.value.scrollLeft = scrollLeftStart.value - walk
}

const onScroll = () => {
    if (!scrollContainer.value || !props.infinite) return
    
    const { scrollLeft, scrollWidth, offsetWidth } = scrollContainer.value
    // Logic assumes content is duplicated 2x
    // If we scroll past the first half (the original set), reset to start
    // Note: This matches the logic previously in startAutoScroll
    if (scrollLeft >= (scrollWidth / 2)) {
        // Smooth loop back to 0? No, instant jump to 0 (which is visually identical)
        // Adjust by subtracting the half-width
        scrollContainer.value.scrollLeft -= (scrollWidth / 2)
    }
    // Optional: Left-side loop (if we scroll back past 0)
    // if (scrollLeft <= 0) {
    //    scrollContainer.value.scrollLeft += (scrollWidth / 2)
    // }
}

// Expose checks for children to prevent clicks
const wasDragging = computed(() => isDragging.value)
defineExpose({
    next: () => scroll('right'),
    prev: () => scroll('left'),
    scrollTo: (x: number) => scrollContainer.value?.scrollTo({ left: x, behavior: 'smooth' }),
    wasDragging
})

watch(() => props.autoScroll, (val) => {
    if (val && canScroll.value) startAutoScroll()
    else stopAutoScroll()
})

// Fix: Restart carousel when mouse leaves (even after a click interaction)
watch(isHovered, (hovering) => {
    if (!hovering && props.autoScroll && !props.paused && canScroll.value) {
        // Mouse left the carousel area, restart if not already running
        if (!autoScrollAnimation.value) {
            startAutoScroll()
        }
    }
})
</script>

<template>
  <div 
    class="relative group/carousel w-full"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
    @touchstart="isHovered = true"
    @touchend="isHovered = false"
  >
    <!-- Navigation Buttons - Only if content overflows -->
    <template v-if="showNavigation && canScroll">
      <button 
        @click="scroll('left')"
        class="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-8 z-30 w-12 h-12 rounded-full bg-oxo-card-bg shadow-xl border border-oxo-border items-center justify-center text-foreground hover:text-oxo-red transition-all duration-300 opacity-0 group-hover/carousel:opacity-100 disabled:opacity-0"
        aria-label="Précédent"
      >
        <i class="fas fa-chevron-left" aria-hidden="true"></i>
      </button>
      
      <button 
        @click="scroll('right')"
        class="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-8 z-30 w-12 h-12 rounded-full bg-oxo-card-bg shadow-xl border border-oxo-border items-center justify-center text-foreground hover:text-oxo-red transition-all duration-300 opacity-0 group-hover/carousel:opacity-100 disabled:opacity-0"
        aria-label="Suivant"
      >
        <i class="fas fa-chevron-right" aria-hidden="true"></i>
      </button>
    </template>

    <!-- Scroll Area -->
    <div 
      :class="[
        'relative w-full overflow-hidden',
        (gradientEdges && canScroll) ? 'mask-linear-fade' : ''
      ]"
    >
      <component 
        :is="containerTag"
        ref="scrollContainer"
        class="flex overflow-x-auto gap-4 md:gap-6 pb-8 pt-4 hide-scrollbar px-6 md:px-0 m-0 list-none"
        :class="[isDown ? 'cursor-grabbing' : 'cursor-grab']"
        :style="{ 'scroll-snap-type': isDown ? 'none' : '' }"
        style="scrollbar-width: none; -ms-overflow-style: none;"
        @mousedown="startDrag"
        @scroll="onScroll"
      >
        <slot />
      </component>
    </div>
  </div>
</template>

<style scoped>
.hide-scrollbar::-webkit-scrollbar {
    display: none;
}

.mask-linear-fade {
  mask-image: linear-gradient(to right, transparent 0%, black 5px, black calc(100% - 5px), transparent 100%);
  -webkit-mask-image: linear-gradient(to right, transparent 0%, black 5px, black calc(100% - 5px), transparent 100%);
}

@media (min-width: 768px) {
  .mask-linear-fade {
    mask-image: linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%);
    -webkit-mask-image: linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%);
  }
}

/* Ensure slot content doesn't break flex */
:deep(> *) {
    flex-shrink: 0;
}
</style>
